import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { Protected } from '../data/auth';
import { createRequest, fetchJSON } from '../data/fetch';
import { DateField } from '../components/input';
import Loading from '../components/Loading';
import { format, moneyToStr } from '../../utils';

const COMPANIES = ['Easy Gifts', 'Spark'];

async function create(body) {
  const res = await fetch(createRequest('POST', '/macmaorder/create', body));
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || res.status);
  return json;
}

function Filters({ value, submit }) {
  return <Formik initialValues={value} enableReinitialize onSubmit={submit}>{({ handleSubmit, isSubmitting }) => (
    <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
      <DateField name="from" label="Időszak kezdete" />
      <DateField name="to" label="Időszak vége" />
      <Button variant="contained" type="submit" disabled={isSubmitting}>Keresés</Button>
    </form>
  )}
  </Formik>;
}

function CompanyOrder({ company, data, period, bcurl, refresh }) {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const { rows, total } = data;

  const generate = async () => {
    if (!window.confirm(`${company}: ${total.open} szállítás, ${moneyToStr(total.price)} megrendelés létrehozása a Macma BC-ben?`)) return;
    setBusy(true);
    try {
      const ids = rows.filter(r => !r.billed).map(r => r.key);
      const ret = await create({ company, ...period, ids });
      if (ret.errors.length) {
        enqueueSnackbar(`${ret.no}: ${ret.errors.length} sor hibás (${ret.errors.map(e => e.key).join(', ')})`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Megrendelés létrehozva: ${ret.no}`, { variant: 'success' });
      }
      if (bcurl) window.open(bcurl.replace('macmaorder', ret.no), 'macmaorder');
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' });
    }
    setBusy(false);
    refresh();
  }

  return <Paper sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>{company}</Typography>
      <Button variant="contained" onClick={generate} disabled={busy || !total.open}>
        Megrendelés generálása
      </Button>
    </Box>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Dátum</TableCell>
          <TableCell>Irány</TableCell>
          <TableCell>Partner</TableCell>
          <TableCell>Jármű</TableCell>
          <TableCell align="right">Karton</TableCell>
          <TableCell align="right">Raklap</TableCell>
          <TableCell>Rendelésszám</TableCell>
          <TableCell align="right">Ár</TableCell>
          <TableCell>Számlázva</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(r => <TableRow key={r.key} sx={r.billed ? { opacity: 0.5 } : undefined}>
          <TableCell>{format.date(r.date)}</TableCell>
          <TableCell>{r.type}</TableCell>
          <TableCell>{r.partner}</TableCell>
          <TableCell>{r.asset}</TableCell>
          <TableCell align="right">{r.cartons}</TableCell>
          <TableCell align="right">{r.palettes}{r.palettes && r.palette_type ? ` ${r.palette_type}` : ''}</TableCell>
          <TableCell>{r.order}{r.parts > 1 ? ` (${r.part}/${r.parts})` : ''}</TableCell>
          <TableCell align="right">{moneyToStr(r.price)}</TableCell>
          <TableCell>{r.billed || ''}</TableCell>
        </TableRow>)}
        <TableRow>
          <TableCell colSpan={4}><b>Összesen: {total.count} szállítás ({total.open} elszámolatlan sor)</b></TableCell>
          <TableCell align="right"><b>{total.cartons}</b></TableCell>
          <TableCell align="right"><b>{total.palettes}</b></TableCell>
          <TableCell />
          <TableCell align="right"><b>{moneyToStr(total.price)}</b></TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  </Paper>
}

function MacmaOrderWindow() {
  const [period, setPeriod] = useState({});
  const [data, setData] = useState(false);

  const load = useCallback(async (p) => {
    setData(false);
    const d = await fetchJSON('GET', '/macmaorder/list', p);
    if (d) {
      setPeriod({ from: d.from, to: d.to });
      setData(d);
    }
  }, []);

  useEffect(() => { load({}) }, [load]);

  return <Box sx={{ p: 2, bgcolor: 'background.default', minHeight: '100vh' }}>
    <Typography variant="h5" sx={{ mb: 2 }}>Macma szállítások elszámolása (Easy Gifts, Spark)</Typography>
    <Box sx={{ mb: 2 }}>
      <Filters value={period} submit={load} />
    </Box>
    {!data ? <Loading /> : COMPANIES.map(c =>
      <CompanyOrder key={c} company={c} data={data.companies[c]} period={period} bcurl={data.bcurl} refresh={() => load(period)} />
    )}
  </Box>
}

const MacmaOrderApp = () => <Protected module="Macma elszámolás" allowed={u => u.admin}>
  <MacmaOrderWindow />
</Protected>

export default MacmaOrderApp;
